import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  type ActivatedRouteSnapshot,
  NavigationEnd,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
} from '@angular/router';
import { DeviceHelperService, WINDOW } from '@tvd/cdk/browser';
import { AuthUser } from '@tvd/data-access/auth';
import { SubscriptionStore } from '@tvd/data-access/subscription';
import { ProgressSpinner } from '@tvd/design-system/progress-spinner';
import { LoadingService } from '@tvd/cdk/loading';
import { Footer } from '@tvd/ui/footer/footer';
import { EMPTY, interval } from 'rxjs';
import {
  catchError,
  endWith,
  filter,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

@Component({
  selector: '[zynRootContainer]',
  host: {
    class: 'w-full overflow-y-auto sm:pb-0',
    '[style.--bump]': 'bottomBarShown() ? "72px" : "0px"',
  },
  styles: [
    `
      :host {
        --bump: 0px;
        height: calc(
          100dvh - env(safe-area-inset-bottom, 0px) - var(--bump) - var(
              --trial-banner-h,
              0px
            )
        );
      }
    `,
  ],
  imports: [ProgressSpinner, Footer],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @if (loading()) {
      <div
        class="fixed z-[1000005] inset-0 h-screen w-screen flex justify-center items-center bg-background"
      >
        <tvd-progress-spinner size="lg" />
      </div>
    }
    <ng-content />
    @if (footer()) {
      <div class="my-12">
        <zyn-footer [showBranding]="footer().showBranding" />
      </div>
    }
  `,
})
export class RootContainer {
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #winRef = inject(WINDOW);
  readonly #loadingService = inject(LoadingService);
  readonly loading = this.#loadingService.isLoading;
  readonly user = inject(AuthUser).data;
  readonly isSM = inject(DeviceHelperService).isSM;
  readonly bottomBarShown = computed(() => !this.isSM() && this.user());

  private readonly subscriptionStore = inject(SubscriptionStore);

  readonly activeSubscription = computed(() =>
    this.subscriptionStore.hasActiveSubscription(),
  );
  readonly bannerShown = computed(
    () =>
      this.user() &&
      (!this.subscriptionStore.hasActiveSubscription() ||
        this.subscriptionStore.trialDaysLeft() > 0),
  );

  readonly #events = this.#router.events;

  /** In case we need a global lazy loading spinner */
  readonly #lazyLoadingStart = this.#events.pipe(
    filter((e) => e instanceof RouteConfigLoadStart),
  );
  readonly #lazyLoadingEnd = this.#events.pipe(
    filter((e) => e instanceof RouteConfigLoadEnd),
  );
  readonly #lazyLoading = this.#lazyLoadingStart.pipe(
    switchMap(() =>
      interval(1).pipe(
        map((millis) => millis > 300),
        takeUntil(this.#lazyLoadingEnd),
        endWith(false),
        catchError(() => {
          this.#winRef.location.reload();
          return EMPTY;
        }),
      ),
    ),
  );
  // readonly lazyLoading = toSignal(this.#lazyLoading);

  readonly footer = toSignal(
    this.#events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.#getData('hasFooter')),
    ),
  );

  #getData(key: string) {
    let current = this.#route.snapshot as ActivatedRouteSnapshot | null;
    while (current) {
      const data = current.data;
      if (data[key]) {
        return data[key];
      }
      current = current.firstChild;
    }
    return undefined;
  }
}
